/* ──────────────────────────────────────────────────────────────
 *  MoreDetailsDialog — Shows detailed fields for selected sample
 *
 *  Displays a 2-column Field/Value table using the bushing-type-
 *  specific field mapping (same data as legacy Python "More Details").
 * ────────────────────────────────────────────────────────────── */

import React, { useMemo } from 'react';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import DialogShell from '@/components/DialogShell';
import { useUIStore } from '@/stores/useUIStore';
import { useBushingStore } from '@/stores/useBushingStore';
import { useSampleStore } from '@/stores/useSampleStore';
import { useInputStore } from '@/stores/useInputStore';
import { useConfigStore } from '@/stores/useConfigStore';
import type { SampleRow } from '@/types/sample';
import type { FieldMapping } from '@/types/bushing';

interface DetailItem {
  field: string;
  value: string;
}

function buildDetailItems(
  row: SampleRow | null,
  fieldMapping: FieldMapping,
  packaging: { coreId: number | null },
  clampLoad: number | null,
): DetailItem[] {
  if (!row) {
    return [{ field: 'Info', value: 'Select a New (orange) sample first.' }];
  }

  const items: DetailItem[] = [];

  for (const [key, label] of Object.entries(fieldMapping)) {
    let val: unknown;

    if (key === '_core_id_input') {
      val = packaging.coreId;
    } else if (key === '_clamp_load_input') {
      val = clampLoad;
    } else if (key === 'RADIAL_SWAGING_MM') {
      // Special: displayed as diameter = value * 2
      const raw = row[key] ?? row['DIAMETER_SWAGING'];
      val = raw != null ? Number(raw) * 2 : null;
    } else {
      val = row[key];
    }

    let display: string;
    if (val == null || val === '') {
      display = '—';
    } else {
      const num = Number(val);
      display = isNaN(num) ? String(val) : num.toFixed(2);
    }

    items.push({ field: label, value: display });
  }

  return items;
}

const MoreDetailsDialog: React.FC = () => {
  const open = useUIStore((s) => s.moreDetailsDialogOpen);
  const closeDialog = useUIStore((s) => s.closeDialog);

  const detailsFieldMapping = useBushingStore((s) => s.detailsFieldMapping);
  const currentSelectedId = useSampleStore((s) => s.currentSelectedId);
  const genRowsById = useSampleStore((s) => s.genRowsById);
  const packaging = useInputStore((s) => s.packaging);
  const clampLoad = useConfigStore((s) => s.clampLoad);

  const row = useMemo((): SampleRow | null => {
    if (!currentSelectedId) return null;
    return genRowsById[currentSelectedId] ?? null;
  }, [currentSelectedId, genRowsById]);

  const items = useMemo(
    () => buildDetailItems(row, detailsFieldMapping, packaging, clampLoad),
    [row, detailsFieldMapping, packaging, clampLoad],
  );

  const handleClose = () => closeDialog('moreDetailsDialog');

  const title = currentSelectedId
    ? `Details for sample — ${currentSelectedId}`
    : 'Details for sample';

  return (
    <DialogShell
      open={open}
      onClose={handleClose}
      title={title}
      titleColor="primary.main"
      maxWidth={500}
      actions={
        <Button variant="contained" color="info" onClick={handleClose}>
          Close
        </Button>
      }
    >
      {items.length === 1 && items[0].field === 'Info' ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          {items[0].value}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.field}>
                  <TableCell>{item.field}</TableCell>
                  <TableCell>{item.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </DialogShell>
  );
};

export default MoreDetailsDialog;
